import { useMediaQuery, useTheme } from '@mui/material';
import type { AxisProps } from '@nivo/axes';
import { type Datum, type LineProps, type Serie } from '@nivo/line';
import type {
  ValuationGrowthResponse,
  ValuationTimelineResponse,
} from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { TrendArrow } from '@public/components/TrendArrow/TrendArrow';
import { Text } from '@public/components/UI/Typography/Text';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';

import {
  NilChartTooltip,
  type NilChartTooltipProps,
  percentChange,
} from './NilChartTooltip/NilChartTooltip';
import styles from './NilGrowth.module.scss';

const ResponsiveLine = dynamic(
  () => import('@nivo/line').then((m) => m.ResponsiveLine),
  { ssr: false },
);

const formattedData = (
  list: ValuationTimelineResponse[],
): { max: number; data: Serie['data'] } =>
  list.reduce(
    (acc, curr) => {
      if (acc.data.some((d) => d.x === curr.date)) {
        return { ...acc };
      }

      return {
        ...acc,
        data: [...acc.data, { x: curr.date, y: curr.valuation }],
        max:
          curr.valuation && curr.valuation > acc.max ? curr.valuation : acc.max,
      };
    },
    { max: 0, data: [] as Serie['data'] },
  );

const getYScale = (max: number) =>
  ({
    type: 'linear',
    min: 0,
    max: max * 1.1,
    stacked: true,
    reverse: false,
  }) as const;

const xScale: LineProps['xScale'] = { type: 'point' };

const graphMargin = { top: 50, right: 50, bottom: 50, left: 50 };

export const NilGrowth = ({
  intervalAmount,
  intervalType,
  list,
  valuationChange,
  valuationStart,
}: ValuationGrowthResponse) => {
  const theme = useTheme();
  const smMq = useMediaQuery(theme.breakpoints.down('md'));

  const currentValuation = formatCurrency(valuationChange);
  const valuationDelta = percentChange(valuationChange, valuationStart);
  const titleVerbiage = valuationDelta > 0 ? 'Growth' : 'Decline';
  const { max, gridYValues, chartData } = useMemo(() => {
    const { max: maxVal, data } = formattedData(list || []);

    return {
      max: maxVal,
      gridYValues: [
        0,
        Math.round(maxVal / 4),
        Math.round(maxVal / 2),
        Math.round(maxVal / 1.25),
        Math.round(maxVal * 1.1),
      ],
      chartData: [
        {
          id: 'NIL Data',
          color: '#FF0013',
          data,
        },
      ],
    };
  }, [list]);

  const axisLeft: AxisProps = useMemo(
    () => ({ tickValues: gridYValues, format: formatCurrency }),
    [gridYValues],
  );

  const axisBottom: AxisProps = useMemo(() => {
    return {
      tickRotation: smMq ? 90 : 0,
      format: (date: string) =>
        new Date(date).toLocaleDateString('en-us', {
          month: 'short',
          day: '2-digit',
        }),
    };
  }, [smMq]);

  return (
    <div className={styles.container}>
      {!!valuationDelta && (
        <div className={styles.graphHeader}>
          <Text className={styles.graphTitle}>
            {intervalAmount}-{intervalType} NIL {titleVerbiage}:{' '}
            {currentValuation}
          </Text>
          <TrendArrow percentChange={valuationDelta} />
        </div>
      )}
      <ResponsiveLine
        animate={true}
        axisBottom={axisBottom}
        axisLeft={axisLeft}
        axisTop={null}
        colors="#0d8bff"
        curve="monotoneX"
        data={chartData}
        enableGridX={false}
        enableSlices="x"
        gridYValues={gridYValues}
        isInteractive={true}
        margin={graphMargin}
        pointSize={10}
        sliceTooltip={sliceTooltip(chartData)}
        useMesh={true}
        xScale={xScale}
        yScale={getYScale(max)}
      />
    </div>
  );
};

const sliceTooltip: (
  chartData: Datum[],
) => ({ slice }: NilChartTooltipProps) => JSX.Element = (chartData: Datum[]) =>
  function SliceToolTip({ slice }: NilChartTooltipProps) {
    return <NilChartTooltip allChartData={chartData[0].data} slice={slice} />;
  };
